/**
 * Method Client
 * Handles Frappe method calls and utility methods like getCount
 */

import type { MethodCallParams, GetCountParams } from "./types";
import { logger } from "@/lib/utils/logger";
import { CSRFTokenError, SessionExpiredError, AuthenticationError, FrappeApiError } from "@/lib/errors/ErrorTypes";

export interface MethodClientConfig {
  url: string;
  getAuthHeaders: () => Promise<HeadersInit>;
}

export class MethodClient {
  private config: MethodClientConfig;

  constructor(config: MethodClientConfig) {
    this.config = config;
  }

  /**
   * Check if error response indicates CSRF or session error
   */
  private detectSessionError(response: Response, errorData: any): Error | null {
    const status = response.status;
    const errorMessage = errorData?.message || errorData?.exc || errorData?.exc_type || "";
    const errorMessageLower = String(errorMessage).toLowerCase();
    const excType = errorData?.exc_type || "";

    // Check for CSRF token errors (status 400 with CSRF-related indicators)
    // Only match CSRF-specific patterns, not generic "Invalid Request"
    if (
      status === 400 &&
      (
      excType === "CSRFTokenError" ||
        excType.toLowerCase().includes("csrf") ||
      errorMessageLower.includes("csrf") ||
        errorMessageLower.includes("csrftokenerror")
      )
    ) {
      return new CSRFTokenError(
        errorMessage || "CSRF token error. Please refresh the page and try again.",
        errorData
      );
    }

    // Check for session expiration (401 Unauthorized)
    if (
      status === 401 ||
      excType === "SessionExpired" ||
      errorMessageLower.includes("session expired") ||
      errorMessageLower.includes("not logged in") ||
      errorMessageLower.includes("authentication required")
    ) {
      return new SessionExpiredError(errorMessage || "Session expired. Please log in again.", errorData);
    }

    // Check for general authentication errors (403 with auth-related message)
    if (
      status === 403 &&
      (errorMessageLower.includes("unauthorized") ||
       errorMessageLower.includes("permission") ||
       errorMessageLower.includes("authenticated"))
    ) {
      return new AuthenticationError(errorMessage || "Authentication required. Please log in again.", errorData);
    }

    return null;
  }

  /**
   * Call a Frappe method (API endpoint)
   */
  async call<T = any>(params: MethodCallParams): Promise<T> {
    const { method, args } = params;
    const baseUrl = this.config.url.replace(/\/$/, "");
    // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
    // If baseUrl is absolute (http://...), add "/api" prefix
    const apiPrefix = baseUrl === "/api" ? "" : "/api";
    const url = `${baseUrl}${apiPrefix}/method/${method}`;

    const headers = await this.config.getAuthHeaders();

    const response = await fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: args ? JSON.stringify(args) : undefined,
    });

    const data = await response.json();
    if (!response.ok) {
      // Check for CSRF/session errors first
      const sessionError = this.detectSessionError(response, data);
      if (sessionError) {
        throw sessionError;
      }

      // Otherwise throw standard API error
      const errorMessage = data.message || data.exc || data.exc_type || response.statusText;
      throw new FrappeApiError(
        errorMessage,
        response.status,
        response.statusText,
        data
      );
    }

    // Frappe methods return data in the 'message' field
    return data.message || data;
  }

  /**
   * Get count of documents matching filters
   * Uses Frappe's get_count method as fallback when total count is not in list response
   */
  async getCount(params: GetCountParams): Promise<number> {
    const { doctype, filters } = params;
    const baseUrl = this.config.url.replace(/\/$/, "");
    // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
    // If baseUrl is absolute (http://...), add "/api" prefix
    const apiPrefix = baseUrl === "/api" ? "" : "/api";
    const url = `${baseUrl}${apiPrefix}/method/frappe.client.get_count`;

    try {
      const headers = await this.config.getAuthHeaders();

      const response = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          doctype,
          filters: filters ? JSON.stringify(filters) : undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        // Check for CSRF/session errors first
        const sessionError = this.detectSessionError(response, data);
        if (sessionError) {
          throw sessionError;
        }

        // Otherwise throw standard API error
        throw new FrappeApiError(
          data.message || `Failed to get count: ${response.statusText}`,
          response.status,
          response.statusText,
          data
        );
      }

      // Frappe methods return data in the 'message' field
      const count = data.message;
      return typeof count === "number" ? count : 0;
    } catch (error) {
      logger.error("Error getting count from Frappe:", error);
      throw error;
    }
  }
}

